import { capitalize, groupBy, isEmpty, uniqueId } from 'lodash'

import { blockPrefix, FIELDS_TABLE, PERMISSION_TABLE } from './constant'
import { rowActions } from 'pages/Administration/constants'
import { onExportData } from 'pages/Administration/helpers'

export const selectConfig = ({ scenarioModelConfig }) => ({
  config: scenarioModelConfig.wizard?.config ?? {},
  additionalColumns: scenarioModelConfig?.scenario?.additionalColumns ?? []
})

export const getScenarios = (scenarios) =>
  scenarios.filter(({ config }) => config === 'scenario')

export const formatTableData = (scenario, t) =>
  scenario.map((auth) => {
    const permissionsByType = groupBy(auth.permissions, 'scenarioType')
    const child = Object.entries(permissionsByType).map(([key, value]) => ({
      id: PERMISSION_TABLE,
      scenarioType: key,
      name: t(`Wizard:${key}`),
      children: value,
      data: auth
    }))

    return {
      data: auth,
      id: auth.rolename,
      name: auth.rolename,
      children: [
        ...child,
        {
          id: FIELDS_TABLE,
          name: t('ScenarioManagement.Fields'),
          children: auth.fields
        }
      ]
    }
  })

const formatPermissionsChildren = ({ children, data }) =>
  children.map(({ rolename: name, scenarioType, ...permission }) => {
    const item = {
      name,
      permission,
      scenarioType,
      record: data,
      table: PERMISSION_TABLE
    }

    if (name !== 'others') {
      return item
    }

    // the CUDE permissions are not editable for the others field
    return {
      ...item,
      disabled: {
        create: true,
        update: true,
        delete: true,
        import_: true,
        export: true
      }
    }
  })

const formatFieldsChildren = ({ children, data }) =>
  Object.entries(children).map(([name, value]) => ({
    name,
    record: data,
    table: FIELDS_TABLE,
    permission: {
      read: value
    }
  }))

export const formatChildrenMap = {
  [PERMISSION_TABLE]: formatPermissionsChildren,
  [FIELDS_TABLE]: formatFieldsChildren
}

export const getPermissionObject = (list, defaultValue = false) =>
  list.reduce((acc, perm) => ({ ...acc, [perm]: defaultValue }), {})

export const validateAddForm = ({ role, blocks }) => {
  let validation = {
    isValid: true,
    errors: {}
  }

  for (const [id, value] of Object.entries(blocks)) {
    if (isEmpty(value?.scenarioTypes)) {
      validation.errors[id] = 'EmptyScenarioType'
    }
  }

  if (isEmpty(role)) {
    validation = {
      isValid: false,
      errors: {
        ...validation.errors,
        role: 'EmptyRole'
      }
    }
  }

  return validation
}

export const getFormItem = (value) => ({
  value,
  label: `ScenarioManagement.${capitalize(value)}`
})

export const formatRowData = ({ rolename: role, fields, permissions }) => {
  const result = {
    role,
    fields: [],
    blocks: {}
  }

  const typeGrouped = groupBy(permissions, 'scenarioType')

  for (const scenarioType in typeGrouped) {
    const id = uniqueId(blockPrefix)
    const mine = permissions.find(
      ({ rolename, scenarioType: scenType }) =>
        scenType === scenarioType && rolename === role
    )
    const others = permissions.find(
      ({ rolename, scenarioType: scenType }) =>
        scenType === scenarioType && rolename === 'others'
    )

    result.blocks[id] = {
      scenarioTypes: [
        {
          label: scenarioType,
          value: scenarioType
        }
      ],
      minePermissions: mine?.read ? [getFormItem('all')] : [],
      otherPermissions: others?.read ? [getFormItem('read')] : []
    }
  }

  result.fields = Object.entries(fields)
    .filter(([_, value]) => value)
    .map(([name]) => ({
      label: name,
      value: name
    }))

  return result
}

export const buildTableActions = ({
  t,
  gridApi,
  columnApi,
  onAdd,
  onDelete
}) => ({
  selected: [
    {
      title: t(rowActions.delete),
      handleClick: onDelete
    }
  ],
  empty: [
    {
      title: t(rowActions.export),
      handleClick: () =>
        onExportData(gridApi, columnApi, t('ScenarioManagement.Title'))
    },
    {
      title: t(rowActions.add),
      handleClick: onAdd
    }
  ]
})
